import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { DateTime } from 'effect';
import { Effect, Request, RequestResolver } from 'effect';

import type { ApiInfrastructureErrors } from '../../../data/error/api';
import type { BackendApiErrorUnion } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import { liftRequest } from '../context';
import { ProductFavouriteListWithProductCodes } from '@vs-shop/schemas/branded/MiscSchema';
import type { HydraCollectionResponseType } from '@vs-shop/schemas/openapi/response';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import { BackendApi } from '../../../service/api/BackendApi';
import type { AuthToken } from '../../../service/auth/AuthToken';
import type { LoggerConfig } from '../../../service/logger/LoggerConfig';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import type * as HttpClient from 'effect/unstable/http/HttpClient';
import * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/product-favorites/products-lists';
const HTTP_METHOD = 'get';

const ListFavouriteListsError = createBackendApiError({
  domain: 'ProductFavouriteList',
  endpoint: 'List',
  path: ENDPOINT,
  identifierField: 'uuid',
} as const);

export type ProductFavouriteListListError = BackendApiErrorUnion<typeof ListFavouriteListsError>;

const listFavouriteListsFetch = Effect.fn(`http.${HTTP_METHOD} ${ENDPOINT}`, {
  kind: 'client',
  attributes: {
    'http.request.method': HTTP_METHOD,
  },
})(function* () {
  const apiClient = yield* BackendApi;
  return yield* apiClient
    .path(ENDPOINT)
    .method(HTTP_METHOD)
    .accept('application/ld+json')
    .fetch({});
});

const listFavouriteLists = () =>
  listFavouriteListsFetch().pipe(
    Effect.map((response) => response.response),
    Effect.tap(addResponseStatusToSpan),
    Effect.withSpan('ValidateResponse'),
    Effect.flatMap(
      HttpClientResponse.matchStatus({
        '2xx': (response) =>
          schemaBodyJsonClean(HydraCollectionResponse(ProductFavouriteListWithProductCodes))(
            response
          ),
        404: (response) => Effect.fail(new ListFavouriteListsError.NotFoundError({ response })),
        orElse: (response) =>
          Effect.fail(new ListFavouriteListsError.ApiRequestError({ response })),
      })
    ),
    Effect.tap((result) =>
      logApiOperation(
        ApiModule.ProductFavouriteListList,
        'Debug',
        `Successfully resolved ${String(result['hydra:member'].length)} favourite lists`
      )
    ),
    Effect.withSpan('api.productFavouriteList.list', {
      kind: 'internal',
    })
  );

type ListFavouriteListsRequest = {
  readonly _tag: 'ListFavouriteListsRequest';
} & Request.Request<
  HydraCollectionResponseType<typeof ProductFavouriteListWithProductCodes>,
  ProductFavouriteListListError | ApiInfrastructureErrors,
  BackendApi | AuthToken | HttpClient.HttpClient | LoggerConfig | DateTime.CurrentTimeZone
>;

const ListFavouriteListsRequest = Request.tagged<ListFavouriteListsRequest>(
  'ListFavouriteListsRequest'
);

const ListFavouriteListsResolver = RequestResolver.fromEffect(
  liftRequest((_request: ListFavouriteListsRequest) => listFavouriteLists())
);

export const requestFavouriteListsWithProductCodes = Effect.request(
  ListFavouriteListsRequest(),
  ListFavouriteListsResolver
);
